import {
  createGameSimulation,
  createNeutralInput,
  type GameState,
  type SuntraceSimulation,
  type VehicleState
} from "../game";
import { createSuntraceAudioController, type SuntraceAudioController } from "../audio";
import { InputController } from "../input/InputController";
import {
  createSuntraceSceneController,
  type RacePresentationSnapshot,
  type RivalPresentationSnapshot,
  type SuntraceQualityPreference,
  type SuntraceSceneController
} from "../render";
import { GameUI, type HudFrame } from "../ui/GameUI";

const BEST_LAP_KEY = "suntrace.best-lap.v1";
const MAXIMUM_RENDER_DELTA = 0.25;

type InterpolatedVehicle = {
  trackProgress: number;
  lateralOffset: number;
  speed: number;
  steering: number;
  heading: number;
  slip: number;
  drift: number;
  charge: number;
  boost: number;
  impact: number;
  impactSide: -1 | 0 | 1;
};

type DebugState = {
  mode: "attract" | "race";
  race: GameState | null;
  cameraMode: ReturnType<SuntraceSceneController["getCameraMode"]> | null;
  metrics: ReturnType<SuntraceSceneController["getMetrics"]> | null;
};

type SuntraceWindow = Window & {
  __SUNTRACE__: {
    ready: boolean;
    start: () => void;
    pause: () => void;
    recover: () => void;
    setQuality?: (quality: SuntraceQualityPreference) => void;
    state: () => DebugState;
  };
};

const lerp = (start: number, end: number, alpha: number): number => start + (end - start) * alpha;

const lerpWrapped = (start: number, end: number, alpha: number): number => {
  const delta = ((end - start + 1.5) % 1) - 0.5;
  return ((start + delta * alpha) % 1 + 1) % 1;
};

const interpolateVehicle = (
  previous: VehicleState,
  current: VehicleState,
  alpha: number
): InterpolatedVehicle => {
  const slip = lerp(previous.slipAngle, current.slipAngle, alpha) / 0.62;
  const driftState = current.driftState === "entering" ? 0.56 : current.driftState === "drifting" ? 1 : current.driftState === "recovering" ? 0.34 : 0;
  return {
    trackProgress: lerpWrapped(previous.trackProgress, current.trackProgress, alpha),
    lateralOffset: lerp(previous.lateralOffset, current.lateralOffset, alpha),
    speed: lerp(previous.speed, current.speed, alpha),
    steering: lerp(previous.steering, current.steering, alpha),
    heading: lerp(previous.heading, current.heading, alpha),
    slip,
    drift: Math.max(driftState, Math.min(1, Math.abs(slip) * 1.45)),
    charge: lerp(previous.driftCharge, current.driftCharge, alpha),
    boost: lerp(previous.feedback.boost, current.feedback.boost, alpha),
    impact: lerp(previous.feedback.impact, current.feedback.impact, alpha),
    impactSide: current.feedback.impactSide
  };
};

const createAttractSnapshot = (time: number): RacePresentationSnapshot => {
  const trackProgress = (0.018 + time * 0.0025) % 1;
  const rivals: RivalPresentationSnapshot[] = Array.from({ length: 5 }, (_, index) => ({
    id: `rival-${String(index + 1)}`,
    trackProgress: (trackProgress + 0.006 + index * 0.004) % 1,
    lateralOffset: (index % 2 === 0 ? -1 : 1) * (1.4 + index * 0.25),
    speed: 18,
    steering: 0,
    heading: 0,
    slip: 0,
    boost: 0
  }));
  return {
    trackProgress,
    lateralOffset: 0,
    speed: 18,
    steering: 0,
    heading: 0,
    slip: 0,
    drift: 0,
    charge: 0,
    boost: 0,
    impact: 0,
    impactSide: 0,
    rivals,
    phase: "attract",
    time
  };
};

const createPresentationSnapshot = (
  simulation: SuntraceSimulation,
  time: number
): RacePresentationSnapshot => {
  const alpha = simulation.interpolationAlpha;
  const player = interpolateVehicle(simulation.previousState.player, simulation.state.player, alpha);
  const rivals = simulation.state.rivals.map((rival, index): RivalPresentationSnapshot => {
    const previous = simulation.previousState.rivals[index] ?? rival;
    const interpolated = interpolateVehicle(previous, rival, alpha);
    return {
      id: rival.id,
      trackProgress: interpolated.trackProgress,
      lateralOffset: interpolated.lateralOffset,
      speed: interpolated.speed,
      steering: interpolated.steering,
      heading: interpolated.heading,
      slip: interpolated.slip,
      boost: interpolated.boost,
      visible: simulation.isVehicleVisible(rival.id)
    };
  });
  const phase = simulation.state.paused
    ? "paused"
    : simulation.state.phase === "finished"
      ? "finished"
      : simulation.state.phase;
  return { ...player, rivals, phase, time };
};

const getGear = (speed: number): number => {
  if (speed < 1) return 0;
  if (speed < 14) return 1;
  if (speed < 27) return 2;
  if (speed < 42) return 3;
  if (speed < 58) return 4;
  if (speed < 76) return 5;
  return 6;
};

const createHudFrame = (
  state: GameState,
  isVehicleVisible: (vehicleId: string) => boolean,
): HudFrame => {
  const countdownNumber = Math.ceil(state.countdownRemaining);
  const countdown = state.phase === "countdown"
    ? String(Math.max(1, countdownNumber))
    : state.raceTime < 0.65
      ? "GO"
      : "";
  const feedback = state.player.feedback;
  const message = feedback.impact > 0.62
    ? "Hold the line"
    : feedback.recovery > 0.45
      ? "Course recovered"
      : feedback.boost > 0.55
        ? "Drift boost"
        : state.player.driftCharge >= 0.98
          ? "Boost ready"
          : "";
  return {
    lap: state.lap,
    totalLaps: state.totalLaps,
    position: state.position,
    racers: state.rivals.length + 1,
    speedKph: state.player.speed * 3.6,
    gear: getGear(state.player.speed),
    driftCharge: state.player.driftCharge,
    boostActive: feedback.boost > 0.2,
    raceTime: state.raceTime,
    lapTime: state.currentLapTime,
    bestLapTime: state.bestLapTime,
    countdown,
    message,
    racePhase: state.phase,
    playerTrackProgress: state.player.trackProgress,
    rivals: state.rivals.map((rival) => ({
      id: rival.id,
      trackProgress: rival.trackProgress,
      visible: isVehicleVisible(rival.id),
    })),
  };
};

const readBestLap = (): number | null => {
  try {
    const value = Number(localStorage.getItem(BEST_LAP_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const writeBestLap = (seconds: number): void => {
  try {
    localStorage.setItem(BEST_LAP_KEY, String(seconds));
  } catch {
    return;
  }
};

const readQualityPreference = (): SuntraceQualityPreference => {
  const preference = new URLSearchParams(window.location.search).get("quality");
  return preference === "low" || preference === "medium" || preference === "high" ? preference : "auto";
};

const isQualityQaMode = (): boolean => {
  const mode = new URLSearchParams(window.location.search).get("qa");
  return mode === "environment-depth" || mode === "image-depth";
};

const readTotalLaps = (): number => {
  const value = Number(new URLSearchParams(window.location.search).get("laps"));
  return Number.isInteger(value) && value >= 1 && value <= 3 ? value : 3;
};

export const restartSuntraceRacePresentation = (
  simulation: Pick<SuntraceSimulation, "reset">,
  scene: Pick<SuntraceSceneController, "resetPresentation"> | null,
): void => {
  simulation.reset();
  scene?.resetPresentation();
};

export const recoverSuntraceRacePresentation = (
  simulation: Pick<SuntraceSimulation, "recoverPlayer">,
  scene: Pick<SuntraceSceneController, "resetPresentation"> | null,
): boolean => {
  const recovered = simulation.recoverPlayer();
  if (recovered) {
    scene?.resetPresentation();
  }
  return recovered;
};

export class SuntraceRuntime {
  readonly #ui: GameUI;
  readonly #input: InputController;
  #scene: SuntraceSceneController | null = null;
  #audio: SuntraceAudioController | null = null;
  #simulation: SuntraceSimulation | null = null;
  #animationFrame = 0;
  #lastFrameTime = 0;
  #elapsedTime = 0;
  #storedBestLap = readBestLap();
  #disposed = false;
  #rendererAvailable = true;
  #initializationController: AbortController | null = null;
  readonly #totalLaps = readTotalLaps();
  readonly #debugWindow = window as unknown as SuntraceWindow;

  constructor(root: HTMLElement) {
    this.#ui = new GameUI(
      root,
      {
        start: this.start,
        resume: this.resume,
        restart: this.restart,
        returnToStart: this.returnToStart
      },
      this.#totalLaps
    );
    this.#input = new InputController(this.#ui.touchRoot);
    this.#debugWindow.__SUNTRACE__ = {
      ready: false,
      start: this.start,
      pause: this.togglePause,
      recover: this.recover,
      ...(isQualityQaMode() ? { setQuality: this.setQuality } : {}),
      state: this.getDebugState
    };
  }

  async initialize(): Promise<void> {
    const initializationController = new AbortController();
    this.#initializationController = initializationController;
    this.#ui.setBootProgress("Shaping Aurelia Pass", 0.25);
    const canvas = document.createElement("canvas");
    canvas.setAttribute("aria-hidden", "true");
    this.#ui.worldHost.append(canvas);
    window.addEventListener("blur", this.#onBlur);
    window.addEventListener("pagehide", this.#onPageHide);
    window.addEventListener("pageshow", this.#onPageShow);
    document.addEventListener("visibilitychange", this.#onVisibilityChange);
    try {
      const scene = await createSuntraceSceneController({
        canvas,
        quality: readQualityPreference(),
        adaptiveQuality: true,
        signal: initializationController.signal,
        onAssetProgress: (progress) => {
          this.#ui.setBootProgress(
            "Loading the Aurelia coast",
            0.25 + progress * 0.65,
          );
        },
        onContextLost: this.#onContextLost,
        onContextRestored: this.#onContextRestored
      });
      if (this.#disposed) {
        scene.dispose();
        return;
      }
      this.#scene = scene;
      this.#ui.setBootProgress("Circuit ready", 1);
      this.#ui.showMenu();
      this.#debugWindow.__SUNTRACE__.ready = true;
      this.#lastFrameTime = performance.now();
      this.#animationFrame = requestAnimationFrame(this.#frame);
    } catch (error) {
      if (!this.#disposed) {
        this.dispose();
      }
      throw error;
    } finally {
      if (this.#initializationController === initializationController) {
        this.#initializationController = null;
      }
    }
  }

  readonly start = (): void => {
    if (!this.#scene) return;
    this.#audio ??= createSuntraceAudioController();
    void this.#audio.unlock();
    const track = this.#scene.getTrackInfo();
    this.#simulation = createGameSimulation({
      trackLength: track.length,
      roadHalfWidth: track.roadHalfWidth,
      totalLaps: this.#totalLaps,
      initialBestLapTime: this.#storedBestLap
    });
    this.#scene.resetPresentation();
    this.#ui.startRace();
  };

  readonly restart = (): void => {
    void this.#audio?.unlock();
    this.#audio?.setMuted(false);
    if (!this.#simulation) {
      this.start();
      return;
    }
    restartSuntraceRacePresentation(this.#simulation, this.#scene);
    this.#ui.startRace();
    this.#ui.setPaused(false);
  };

  readonly resume = (): void => {
    void this.#audio?.unlock();
    this.#audio?.setMuted(false);
    this.#simulation?.setPaused(false);
    this.#ui.setPaused(false);
  };

  readonly returnToStart = (): void => {
    this.#audio?.setMuted(true);
    this.#simulation = null;
    this.#scene?.resetPresentation();
    this.#ui.showMenu();
  };

  readonly togglePause = (): void => {
    if (!this.#simulation || this.#simulation.state.phase === "finished") return;
    const paused = !this.#simulation.state.paused;
    this.#simulation.setPaused(paused);
    this.#ui.setPaused(paused);
    if (!paused) {
      this.#audio?.setMuted(false);
    }
  };

  readonly recover = (): void => {
    if (!this.#simulation) return;
    recoverSuntraceRacePresentation(this.#simulation, this.#scene);
  };

  readonly setQuality = (quality: SuntraceQualityPreference): void => {
    this.#scene?.setQuality(quality);
  };

  readonly dispose = (): void => {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#initializationController?.abort();
    this.#initializationController = null;
    cancelAnimationFrame(this.#animationFrame);
    window.removeEventListener("blur", this.#onBlur);
    window.removeEventListener("pagehide", this.#onPageHide);
    window.removeEventListener("pageshow", this.#onPageShow);
    document.removeEventListener("visibilitychange", this.#onVisibilityChange);
    this.#input.dispose();
    this.#ui.dispose();
    this.#audio?.dispose();
    this.#audio = null;
    this.#scene?.dispose();
    this.#scene = null;
    this.#debugWindow.__SUNTRACE__.ready = false;
  };

  readonly getDebugState = (): DebugState => ({
    mode: this.#simulation ? "race" : "attract",
    race: this.#simulation ? structuredClone(this.#simulation.state) : null,
    cameraMode: this.#scene?.getCameraMode() ?? null,
    metrics: this.#scene?.getMetrics() ?? null
  });

  readonly #onBlur = (): void => {
    this.#audio?.setMuted(true);
    if (!this.#simulation || this.#simulation.state.phase === "finished") return;
    this.#simulation.setPaused(true);
    this.#ui.setPaused(true);
  };

  readonly #onVisibilityChange = (): void => {
    if (document.hidden) {
      this.#onBlur();
    }
  };

  readonly #onContextLost = (): void => {
    this.#rendererAvailable = false;
    this.#onBlur();
  };

  readonly #onContextRestored = (): void => {
    this.#rendererAvailable = true;
    this.#lastFrameTime = performance.now();
  };

  readonly #onPageHide = (event: PageTransitionEvent): void => {
    if (!event.persisted) {
      this.dispose();
      return;
    }
    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = 0;
    this.#onBlur();
  };

  readonly #onPageShow = (event: PageTransitionEvent): void => {
    if (!event.persisted || this.#disposed || !this.#scene || this.#animationFrame !== 0) return;
    this.#lastFrameTime = performance.now();
    this.#animationFrame = requestAnimationFrame(this.#frame);
  };

  readonly #frame = (frameTime: number): void => {
    if (this.#disposed || !this.#scene) return;
    const deltaSeconds = Math.min(MAXIMUM_RENDER_DELTA, Math.max(0, (frameTime - this.#lastFrameTime) / 1_000));
    this.#lastFrameTime = frameTime;
    const presentationActive =
      this.#rendererAvailable &&
      (!this.#simulation ||
        (!this.#simulation.state.paused && this.#simulation.state.phase !== "finished"));
    if (presentationActive) {
      this.#elapsedTime += deltaSeconds;
    }
    const driveInput = this.#input.sample();
    const events = this.#input.consumeEvents();

    if (events.start && !this.#simulation) this.start();
    else if (events.pause) this.togglePause();
    if (events.recover) this.recover();
    if (events.camera) this.#scene.cycleCamera();

    if (this.#simulation) {
      const input = createNeutralInput({ ...driveInput });
      this.#simulation.update(deltaSeconds, input);
      const snapshot = createPresentationSnapshot(this.#simulation, this.#elapsedTime);
      this.#scene.render(snapshot, deltaSeconds);
      this.#audio?.update(snapshot, deltaSeconds);
      this.#ui.update(createHudFrame(
        this.#simulation.state,
        (vehicleId) => this.#simulation?.isVehicleVisible(vehicleId) ?? false,
      ));
      const bestLap = this.#simulation.state.bestLapTime;
      if (bestLap !== null && bestLap !== this.#storedBestLap) {
        this.#storedBestLap = bestLap;
        writeBestLap(bestLap);
      }
    } else {
      this.#scene.render(createAttractSnapshot(this.#elapsedTime), deltaSeconds);
    }

    this.#animationFrame = requestAnimationFrame(this.#frame);
  };
}
